import { ChartData, InsurerAppointments, PAYER_CATEGORY, PayerCategory } from '../types';

/**
 * SHER-900. The appointments-by-insurance card, grouped by who pays rather than by which
 * insurer.
 *
 * CHOM's catalogue runs to dozens of insurers, so the per-insurer pie is five slices and an
 * "Other" that is larger than most of them. "How much of our work is IPM, how much is private,
 * how much is free care" is the question the card is actually asked, and it has seven answers
 * instead of forty.
 */

/**
 * The categories that answer the question. `OTHER_OR_UNKNOWN` and `NO_COVERAGE` are deliberately
 * absent: they are not answers, they are the two ways of having none. They still appear in the
 * chart when present — see `groupByPayerCategory` — but they do not count as evidence that a
 * tenant has classified anything.
 */
const REAL_CATEGORIES: PayerCategory[] = [
  PAYER_CATEGORY.IPM,
  PAYER_CATEGORY.INSURANCE,
  PAYER_CATEGORY.MUTUAL,
  PAYER_CATEGORY.PRIVATE_PATIENT,
  PAYER_CATEGORY.SPECIFIC_PROGRAM,
  PAYER_CATEGORY.STAFF_OR_FREE,
];

/**
 * Whether this tenant has classified any of its insurers.
 *
 * The gate for the whole feature, and the reason it needs no tenant flag. `payerCategory` ships
 * NOT NULL defaulting to `OTHER_OR_UNKNOWN`, and only CHOM has been backfilled — so "nobody has
 * said" is the honest reading everywhere else, and a card grouped into a single
 * "Other/unknown" wedge would be strictly worse than the per-insurer pie those tenants have
 * today. A tenant flips over on its own the moment someone categorises one insurer.
 */
export const hasCategorisedInsurers = (insurers: InsurerAppointments[]): boolean =>
  insurers.some((insurer) => REAL_CATEGORIES.includes(insurer.payerCategory));

/**
 * A chart whose `categories` are payer-category KEYS rather than insurer codes.
 *
 * The tag is the whole point: the two shapes this module can produce are otherwise
 * indistinguishable, because `IPM` is simultaneously a payer category and a real insurer code
 * (CHOM ships one, and so do two other tenants). Anything downstream that needs to know which
 * vocabulary it is holding reads this field — it must never go back to inspecting the strings.
 */
export type GroupedByPayerCategory = ChartData & { categoryKind: 'payerCategory' };

/**
 * Appointments per payer category, largest first, as chart data.
 *
 * Categories with no appointments are absent rather than zero — a pie of empty wedges reads as
 * a broken chart. `OTHER_OR_UNKNOWN` and `NO_COVERAGE` DO appear when they have appointments:
 * on a partly-classified tenant they are a real and useful figure ("this much of our caseload is
 * unaccounted for"), and hiding them would make the slices sum to less than the total.
 */
export const groupByPayerCategory = (insurers: InsurerAppointments[]): GroupedByPayerCategory => {
  const totals = new Map<PayerCategory, number>();

  insurers.forEach((insurer) => {
    totals.set(insurer.payerCategory, (totals.get(insurer.payerCategory) ?? 0) + insurer.appointments);
  });

  const ordered = Array.from(totals.entries())
    .filter(([, appointments]) => appointments > 0)
    .sort((a, b) => b[1] - a[1]);

  return {
    series: [{ name: 'Appointments', data: ordered.map(([, appointments]) => appointments) }],
    categories: ordered.map(([category]) => category),
    count: ordered.reduce((total, [, appointments]) => total + appointments, 0),
    categoryKind: 'payerCategory',
  };
};

/**
 * What the card shows: the grouped summary where the tenant has classified anything, and
 * otherwise exactly what it showed before — the same `props` the endpoint has always returned.
 *
 * Returning the untouched `props` rather than rebuilding an equivalent is deliberate: the
 * fallback path has to be the OLD chart, byte for byte, not a reimplementation of it that can
 * drift. That is also why only the grouped branch is tagged: the absence of `categoryKind` IS
 * the per-insurer case, so the fallback stays the identical object it was handed.
 */
export const payerCategoryChartData = (
  props: ChartData | undefined,
  insurers: InsurerAppointments[] | undefined,
): ChartData | GroupedByPayerCategory => {
  const rows = insurers ?? [];
  if (!hasCategorisedInsurers(rows)) {
    return (props ?? { series: [], categories: [], count: 0 }) as ChartData;
  }
  return groupByPayerCategory(rows);
};
